import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { products } from '../../data/products';
import ProductImageGallery from '../../components/ProductImageGallery';
import RelatedProducts from '../../components/RelatedProducts';

const NintendoJoyconPikachu = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImage, setCurrentImage] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [selectedWristStrap, setSelectedWristStrap] = useState(false);
  const [quantity, setQuantity] = useState(1);
  
  // 格式化价格为新币
  const formatPrice = (price) => {
    // 如果价格已经是格式化的字符串（如 "S$22.90"），直接返回
    if (typeof price === 'string') {
      return price;
    }
    // 如果价格是数字，格式化为 "S$" 格式
    return `S$${price.toFixed(2)}`;
  };

  // 获取当前显示价格（包括附加项目）
  const getCurrentPrice = () => {
    const selectedVersionData = versionOptions.find(v => v.name === selectedVersion);
    let basePrice = selectedVersionData ? selectedVersionData.price : 27.90; // 默认显示最低价格
    
    // 添加附加项目价格
    if (selectedWristStrap) {
      basePrice += 9.90; // Wrist Strap 价格
    }
    
    return basePrice;
  };

  // 获取当前版本名称
  const getCurrentVersionName = () => {
    return selectedVersion || 'Classic Blue & Red'; // 默认版本
  };
  
  // 获取Joycon产品数据
  const joycon = products.find(p => p.name === 'Nintendo Joycon Pikachu');
  
  // 调试信息
  console.log('Found joycon:', joycon);
  if (!joycon) {
    console.log('Available products:', products.map(p => p.name));
  }
  
  // 5个版本图片映射
  const versionImages = useMemo(() => ({
    'Tears of the Kingdom': '/images/shop-products/nintendo-joycon-pikachu/tears-of-the-kingdom.jpg',
    'Transparent Pikachu': '/images/shop-products/nintendo-joycon-pikachu/transparent-pikachu.jpg',
    'Classic Blue & Red': '/images/shop-products/nintendo-joycon-pikachu/classic-blue-red.jpg',
    'Animal Crossing': '/images/shop-products/nintendo-joycon-pikachu/animal-crossing.jpg',
    'Skyward Sword': '/images/shop-products/nintendo-joycon-pikachu/skyward-sword.jpg'
  }), []);
  
  // 版本选项
  const versionOptions = [
    { 
      name: 'Tears of the Kingdom', 
      description: 'Limited Edition',
      image: versionImages['Tears of the Kingdom'],
      price: 43.99,
      inStock: true
    },
    { 
      name: 'Transparent Pikachu', 
      description: 'Limited Edition',
      image: versionImages['Transparent Pikachu'],
      price: 45.99,
      inStock: true
    },
    { 
      name: 'Classic Blue & Red', 
      description: 'Classic Edition',
      image: versionImages['Classic Blue & Red'],
      price: 27.90,
      inStock: true
    },
    { 
      name: 'Animal Crossing', 
      description: 'Special Edition',
      image: versionImages['Animal Crossing'],
      price: 41.99,
      inStock: false
    },
    { 
      name: 'Skyward Sword', 
      description: 'Special Edition',
      image: versionImages['Skyward Sword'],
      price: 41.99,
      inStock: false
    }
  ];
  
  // 更多图片 - 包含主图和所有版本图片（共6张）
  const additionalImages = useMemo(() => [
    '/images/shop-products/nintendo-joycon-pikachu/transparent-pikachu.jpg', // 第1张：主图（使用Transparent Pikachu作为主图）
    versionImages['Tears of the Kingdom'], // 第2张：Tears of the Kingdom 版本
    versionImages['Transparent Pikachu'], // 第3张：Transparent Pikachu 版本
    versionImages['Classic Blue & Red'], // 第4张：Classic Blue & Red 版本
    versionImages['Animal Crossing'], // 第5张：Animal Crossing 版本
    versionImages['Skyward Sword'] // 第6张：Skyward Sword 版本
  ], [versionImages]);

  // 初始化当前图片
  useEffect(() => {
    // 当没有选择版本时，显示主图片
    if (selectedVersion && versionImages[selectedVersion]) {
      setCurrentImage(versionImages[selectedVersion]);
    } else {
      setCurrentImage('/images/shop-products/nintendo-joycon-pikachu/transparent-pikachu.jpg');
    }
  }, [selectedVersion, versionImages]);

  // 相关产品
  const relatedProducts = products.filter(p => 
    p.name !== 'Nintendo Joycon Pikachu' && 
    (p.category === 'controllers' || p.category === 'accessories')
  ).slice(0, 4);

  // 添加到购物车函数
  const handleAddToCart = () => {
    const selectedVersionData = versionOptions.find(v => v.name === selectedVersion);
    const basePrice = selectedVersionData ? selectedVersionData.price : 27.90; // 只保存基础价格
    
    const cartItem = {
      id: joycon.id,
      name: joycon.name,
      price: basePrice, // 只保存基础价格，不包含附加项目
      quantity: quantity,
      version: selectedVersion,
      wristStrap: selectedWristStrap,
      image: currentImage
    };
    
    console.log('Adding to cart:', cartItem);
    addToCart(cartItem);
  };

  // 立即购买函数
  const handleBuyNow = () => {
    // 先添加到购物车
    const selectedVersionData = versionOptions.find(v => v.name === selectedVersion);
    const basePrice = selectedVersionData ? selectedVersionData.price : 27.90; // 只保存基础价格
    
    const cartItem = {
      id: joycon.id,
      name: joycon.name,
      price: basePrice, // 只保存基础价格，不包含附加项目
      quantity: quantity,
      version: selectedVersion,
      wristStrap: selectedWristStrap,
      image: currentImage
    };
    
    addToCart(cartItem);
    
    // 然后跳转到购物车页面
    navigate('/cart');
  };

  // 如果找不到产品数据，显示加载状态
  if (!joycon) {
    return (
      <div className="container mx-auto px-4 py-12 text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Loading...</h1>
          <p className="text-gray-400">Product not found or loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 text-white">
      {/* 面包屑导航 */}
      <div className="text-sm text-gray-400 mb-6">
        <Link to="/" className="hover:text-white">Home</Link> / 
        <Link to="/shop" className="hover:text-white mx-2">Shop</Link> / 
        <Link to="/shop/controllers" className="hover:text-white mr-2">Controllers</Link> / 
        <span className="text-white">{joycon.name}</span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* 左侧产品图片 */}
        <div>
          <ProductImageGallery
            images={additionalImages}
            currentImage={currentImage}
            onImageChange={setCurrentImage}
          />
        </div>
        
        {/* 右侧产品信息 */}
        <div>
          <h1 className="text-3xl font-bold mb-2">{joycon.name}</h1>
          
          {/* 评分 */}
          <div className="flex items-center mb-4">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <span key={i} className={i < Math.floor(joycon.rating) ? "text-yellow-400" : "text-gray-600"}>★</span>
              ))}
            </div>
            <span className="ml-2 text-gray-400">({joycon.reviews} reviews)</span>
          </div>
          
                     {/* 价格 */}
           <div className="mb-6">
             <span className="text-3xl font-bold">{formatPrice(getCurrentPrice())}</span>
             {joycon.originalPrice && (
               <span className="ml-3 text-gray-400 line-through">{formatPrice(joycon.originalPrice)}</span>
             )}
             {joycon.originalPrice && (
               <span className="ml-3 bg-red-600 text-white px-2 py-1 rounded-md text-sm">
                 Save {(() => {
                   const originalPrice = parseFloat(joycon.originalPrice.replace(/^S\$/, '')) || 0;
                   const currentPrice = getCurrentPrice();
                   const saveAmount = originalPrice - currentPrice;
                   return formatPrice(saveAmount);
                 })()}
               </span>
             )}
             <div className="text-sm text-gray-400 mt-2">
               Current Selection: {getCurrentVersionName()}
             </div>
           </div>
          
          {/* 库存状态 */}
          <div className="mb-6">
            {joycon.inStock ? (
              <span className="text-green-500 flex items-center">
                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                In Stock
              </span>
            ) : (
              <span className="text-red-500 flex items-center">
                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                Out of Stock
              </span>
            )}
          </div>
          
          {/* 简短描述 */}
          <p className="text-gray-300 mb-6">{joycon.description}</p>
          
          {/* 关键特性 */}
          {joycon.features && joycon.features.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-3 text-white">Key Features</h3>
              <div className="space-y-2">
                {joycon.features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <svg className="w-5 h-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* 选择版本 - 图片选择器 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">Choose Version</h3>
            <div className="flex gap-4 flex-wrap">
              {versionOptions.map((version) => (
                <div key={version.name} className="flex flex-col">
                  <button 
                    className={`w-[80px] h-[80px] border ${version.name === selectedVersion ? 'border-2 border-green-500' : 'border border-gray-600'} 
                      rounded-md bg-gray-800 flex items-center justify-center overflow-hidden relative hover:border-green-400 transition-colors aspect-square ${!version.inStock ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => {
                      if (version.inStock) {
                        setSelectedVersion(version.name);
                        setCurrentImage(version.image);
                      }
                    }}
                    disabled={!version.inStock}
                  >
                    <div className="w-full h-full flex items-center justify-center">
                      <img 
                        src={version.image}
                        alt={version.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/images/products/placeholder.jpg';
                        }}
                      />
                    </div>
                    {version.name === selectedVersion && (
                      <div className="absolute top-0 right-0 w-0 h-0 border-t-[20px] border-r-[20px] border-t-transparent border-r-green-500"></div>
                    )}
                    {!version.inStock && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span className="text-white text-xs font-bold bg-red-600 px-2 py-1 rounded">SOLD OUT</span>
                      </div>
                    )}
                  </button>
                  <div className="mt-2 text-left max-w-[80px]">
                    <div className="text-sm text-white font-medium leading-tight">{version.name}</div>
                    <div className="text-xs text-gray-400 leading-tight mt-1">{version.description}</div>
                    <div className="text-xs text-green-500 font-medium mt-1">{formatPrice(version.price)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          


           {/* Add On 选项 */}
           <div className="mb-6">
             <h3 className="text-lg font-medium mb-3">Add On</h3>
             <div className="space-y-3">
               {/* Wrist Strap 选项 */}
               <button 
                 className={`w-full border rounded-lg p-4 transition-all duration-200 ${
                   selectedWristStrap 
                     ? 'border-green-500 bg-green-500/10' 
                     : 'border-gray-700 hover:border-green-400'
                 }`}
                 onClick={() => setSelectedWristStrap(!selectedWristStrap)}
               >
                 <div className="flex items-center justify-between">
                   <div className="flex items-center space-x-3">
                     <img 
                       src="https://images.unsplash.com/photo-1600080972464-8e5f35f63d08?w=80&h=80&fit=crop&text=Wrist+Strap"
                       alt="Wrist Strap"
                       className="w-16 h-16 rounded-md object-cover"
                       onError={(e) => {
                         e.target.src = '/images/products/placeholder.jpg';
                       }}
                     />
                     <div className="text-left">
                       <div className="text-white font-medium">Wrist Strap</div>
                       <div className="text-gray-400 text-sm">Comfortable wrist straps for extended gaming</div>
                     </div>
                   </div>
                   <div className="text-right">
                     <div className="text-green-500 font-medium">+ S$9.90</div>
                     {selectedWristStrap && (
                       <div className="text-green-500 text-sm mt-1">✓ Selected</div>
                     )}
                   </div>
                 </div>
               </button>
             </div>
           </div>
          
          {/* Quantity */}
          <div className="mb-8">
            <h3 className="text-lg font-medium mb-3">Quantity</h3>
            <div className="flex items-center">
              <button 
                className="bg-gray-800 px-4 py-2 rounded-l-md"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
              >
                -
              </button>
              <input 
                type="text" 
                value={quantity} 
                readOnly
                className="bg-gray-800 text-center w-16 py-2 border-x border-gray-700"
              />
              <button 
                className="bg-gray-800 px-4 py-2 rounded-r-md"
                onClick={() => setQuantity(quantity + 1)}
              >
                +
              </button>
            </div>
          </div>
          
          {/* Purchase Buttons */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button 
              className={`py-3 px-6 rounded-md font-medium transition-colors ${
                selectedVersion 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
              onClick={handleAddToCart}
              disabled={!selectedVersion}
            >
              {selectedVersion ? 'Add to Cart' : 'Please Select Colour'}
            </button>
            <button 
              className={`py-3 px-6 rounded-md font-medium transition-colors ${
                selectedVersion 
                  ? 'bg-gray-800 hover:bg-gray-700 text-white border border-gray-600' 
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed border border-gray-600'
              }`}
              onClick={handleBuyNow}
              disabled={!selectedVersion}
            >
              {selectedVersion ? 'Buy Now' : 'Please Select Colour'}
            </button>
          </div>
          
          {/* Shipping Info */}
          <div className="border-t border-gray-700 pt-6">
            <div className="flex items-center mb-3">
              <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2a1 1 0 00.9-.5l1.5-2A1 1 0 0015 7h-3V4a1 1 0 00-1-1H3zM14 7l-1.5 2H10V5h4v2z" />
              </svg>
              <span className="text-gray-300">Free Meet Up (Available at Sengkang & Orchard locations)</span>
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-300">Available Shipping</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* 产品详情选项卡 */}
      <div className="mt-16">
        <div className="border-b border-gray-700 mb-8">
          <div className="flex space-x-8">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`pb-4 px-2 font-medium ${activeTab === 'overview' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
            >
              Overview
            </button>
            <button 
              onClick={() => setActiveTab('specs')}
              className={`pb-4 px-2 font-medium ${activeTab === 'specs' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
            >
              Specifications
            </button>
            <button 
              onClick={() => setActiveTab('reviews')}
              className={`pb-4 px-2 font-medium ${activeTab === 'reviews' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
            >
              Reviews ({joycon.reviews})
            </button>
          </div>
        </div>
        
        {/* 选项卡内容 */}
        <div>
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Product Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <p className="text-gray-300 mb-6">
                    The Nintendo Joycon Pikachu Edition brings the beloved Pokémon character to life in your gaming experience. 
                    These specially designed controllers feature Pikachu's iconic yellow color scheme with unique detailing 
                    that makes them stand out from standard Joycon controllers.
                  </p>
                  <p className="text-gray-300 mb-6">
                    Perfect for both casual and competitive gaming, these Joycon controllers offer the same precision and 
                    responsiveness as the original Nintendo Switch controllers. The ergonomic design ensures comfortable 
                    gameplay during extended sessions, while the wireless connectivity provides freedom of movement.
                  </p>
                  <p className="text-gray-300">
                    Whether you're playing Mario Kart, Super Smash Bros, or any other Switch game, these Pikachu-themed 
                    Joycon controllers will add a touch of personality to your gaming setup. They're also fully compatible 
                    with all Nintendo Switch models and games.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4">Key Features</h3>
                  <ul className="space-y-4">
                    {joycon.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-6 h-6 mr-2 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-medium">{feature}</span>
                          <p className="text-gray-400 text-sm mt-1">
                            {feature === "Pikachu Design" && "Unique yellow color scheme with Pikachu-inspired detailing"}
                            {feature === "HD Rumble" && "Advanced haptic feedback technology for immersive gaming experience"}
                            {feature === "Motion Controls" && "Precise motion sensing for games that require movement"}
                            {feature === "Wireless Connectivity" && "Bluetooth connection with up to 20 hours battery life"}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'specs' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Technical Specifications</h2>
              <div className="bg-gray-800 rounded-lg p-6">
                <table className="w-full">
                  <tbody>
                    {Object.entries(joycon.specs).map(([key, value], index) => (
                      <tr key={index} className={index !== 0 ? "border-t border-gray-700" : ""}>
                        <td className="py-4 pr-4 font-medium w-1/3">{key}</td>
                        <td className="py-4 text-gray-300">{value}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-gray-700">
                      <td className="py-4 pr-4 font-medium">Dimensions</td>
                      <td className="py-4 text-gray-300">102 x 37.3 x 24.9 mm</td>
                    </tr>
                    <tr className="border-t border-gray-700">
                      <td className="py-4 pr-4 font-medium">Weight</td>
                      <td className="py-4 text-gray-300">49.3 g</td>
                    </tr>
                    <tr className="border-t border-gray-700">
                      <td className="py-4 pr-4 font-medium">Battery Life</td>
                      <td className="py-4 text-gray-300">Up to 20 hours</td>
                    </tr>
                    <tr className="border-t border-gray-700">
                      <td className="py-4 pr-4 font-medium">Warranty</td>
                      <td className="py-4 text-gray-300">1 Year Limited</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Customer Reviews</h2>
              <div className="flex items-center mb-8">
                <div className="mr-4">
                  <div className="text-5xl font-bold">{joycon.rating}</div>
                  <div className="flex text-yellow-400 mt-2">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className={i < Math.floor(joycon.rating) ? "text-yellow-400" : "text-gray-600"}>★</span>
                    ))}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">Based on {joycon.reviews} reviews</div>
                </div>
                <div className="flex-1 ml-6">
                  {[5, 4, 3, 2, 1].map((star) => (
                    <div key={star} className="flex items-center mb-2">
                      <div className="w-12 text-sm text-gray-400">{star} stars</div>
                      <div className="w-full bg-gray-700 rounded-full h-2 mx-2">
                        <div 
                          className="bg-yellow-400 h-2 rounded-full" 
                          style={{ 
                            width: `${star === 5 ? 75 : star === 4 ? 15 : star === 3 ? 7 : star === 2 ? 2 : 1}%` 
                          }}
                        ></div>
                      </div>
                      <div className="w-12 text-sm text-right text-gray-400">
                        {star === 5 ? '75%' : star === 4 ? '15%' : star === 3 ? '7%' : star === 2 ? '2%' : '1%'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* 示例评论 */}
              <div className="space-y-6">
                {[
                  { name: "Emma L.", rating: 5, date: "1 week ago", comment: "These Pikachu Joycon controllers are absolutely adorable! The yellow color is perfect and they work flawlessly with my Switch. The HD rumble feels great and the battery life is impressive." },
                  { name: "David K.", rating: 5, date: "2 weeks ago", comment: "As a huge Pokémon fan, I had to get these Joycon controllers. They're not just cute, they're also very functional. The motion controls are precise and the wireless connection is stable." },
                  { name: "Lisa M.", rating: 4, date: "1 month ago", comment: "Great controllers with a fun design. The only minor issue is that the yellow color shows fingerprints more easily than darker colors, but that's expected." }
                ].map((review, index) => (
                  <div key={index} className="border-b border-gray-700 pb-6">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">{review.name}</span>
                      <span className="text-gray-400 text-sm">{review.date}</span>
                    </div>
                    <div className="flex text-yellow-400 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className={i < review.rating ? "text-yellow-400" : "text-gray-600"}>★</span>
                      ))}
                    </div>
                    <p className="text-gray-300">{review.comment}</p>
                  </div>
                ))}
              </div>
              
              {/* 查看更多评论按钮 */}
              <div className="mt-8 text-center">
                <button className="bg-gray-800 hover:bg-gray-700 text-white py-3 px-6 rounded-md font-medium">
                  Load More Reviews
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* 相关产品 */}
      <div className="mt-20">
        <h2 className="text-2xl font-bold mb-8">You May Also Like</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {relatedProducts.map(product => (
            <div key={product.id} className="bg-gray-800 rounded-lg overflow-hidden">
              <img src={product.image} alt={product.name} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="font-medium mb-2">{product.name}</h3>
                <div className="flex justify-between items-center">
                  <span className="font-bold">${product.price}</span>
                  <button className="bg-green-600 hover:bg-green-700 text-white py-1 px-3 rounded-md text-sm">
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* 相关产品 */}
      <RelatedProducts 
        currentProductId={1} 
        currentCategory="nintendo-switch" 
        limit={4} 
      />
    </div>
  );
};

export default NintendoJoyconPikachu;
